import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { FeedEvent } from '../api/types';
import { FollowButton } from '../components/FollowButton';
import { PlaylistCard } from '../components/cards';
import { IconPlus, IconPulse, IconSearch } from '../components/icons';
import { Avatar, EmptyState, ErrorState, Loading, SectionTitle } from '../components/ui';
import { useAsync } from '../hooks/useAsync';
import { relativeTime } from '../lib/format';
import { t, type MsgKey } from '../i18n';

const KIND_KEYS: Record<FeedEvent['kind'], MsgKey> = {
  playlist_created: 'feed.kindCreated',
  playlist_updated: 'feed.kindUpdated',
};

function FeedRow({ event }: { event: FeedEvent }) {
  return (
    <li className="feed-item">
      <div className="feed-item__head">
        <Avatar name={event.actor.displayName} src={event.actor.avatarUrl} />
        <div className="feed-item__identity">
          <Link to={`/u/${event.actor.username}`}>{event.actor.displayName}</Link>
          <p className="feed-item__kind">{t(KIND_KEYS[event.kind])} · {relativeTime(event.at)}</p>
        </div>
      </div>
      <PlaylistCard playlist={event.playlist} to={`/p/${event.playlist.id}`} />
    </li>
  );
}

export function FeedPage() {
  const state = useAsync(() => api.feed(), [], { keepDataOnReload: true });
  const [dismissed, setDismissed] = useState<string[]>([]);

  if (state.error) {
    return (
      <div className="page">
        <ErrorState error={state.error} onRetry={state.reload} />
      </div>
    );
  }

  const data = state.data;
  const suggestions = (data?.suggestions ?? []).filter((user) => !dismissed.includes(user.id));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t('nav.feed')}</h1>
          <p className="page-head__sub">{t('feed.subtitle')}</p>
        </div>
        <Link className="btn btn--outline" to="/app/search">
          <IconSearch size={17} /> {t('feed.findPeople')}
        </Link>
      </div>

      {state.loading || !data ? (
        <Loading rows={4} />
      ) : data.events.length === 0 ? (
        <EmptyState
          icon={<IconPulse />}
          title={t('feed.emptyTitle')}
          body={t('feed.emptyBody')}
          action={
            <div className="row" style={{ justifyContent: 'center' }}>
              <Link className="btn btn--primary" to="/app/playlists/new">
                <IconPlus size={18} /> {t('feed.createPlaylist')}
              </Link>
              <Link className="btn btn--outline" to="/app/search">
                <IconSearch size={17} /> {t('feed.findPeople')}
              </Link>
            </div>
          }
        />
      ) : (
        <ul className="feed-list">
          {data.events.map((event) => (
            <FeedRow key={event.id} event={event} />
          ))}
        </ul>
      )}

      {suggestions.length > 0 ? (
        <section className="section" aria-labelledby="suggestions">
          <SectionTitle>
            <span id="suggestions">{t('feed.whoToRead')}</span>
          </SectionTitle>
          <div className="panel stack-sm">
            {suggestions.map((suggestion) => (
              <div className="user-row" key={suggestion.id}>
                <Avatar name={suggestion.displayName} src={suggestion.avatarUrl} />
                <div className="user-row__main">
                  <Link to={`/u/${suggestion.username}`} className="strong clamp-1">
                    {suggestion.displayName}
                  </Link>
                  <p className="tiny muted clamp-1">
                    @{suggestion.username}
                    {suggestion.bio ? ` · ${suggestion.bio}` : ''}
                  </p>
                </div>
                <FollowButton
                  userId={suggestion.id}
                  following={false}
                  size="sm"
                  onToggle={(isFollowing) => {
                    setDismissed((current) =>
                      isFollowing ? [...current, suggestion.id] : current.filter((id) => id !== suggestion.id),
                    );
                    // The feed is literally a list of who you follow — it must change with this button.
                    state.reload();
                  }}
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
